/**
 * Audit History Adapter
 * Transforms between API representations (Swagger) and UI representations (domain models)
 * 
 * Transforms Audit API responses (AuditHistoryDocument[]) into UI format (AuditEvent[])
 * Part of the boundary layer between API and UI domains
 */

import type { AuditHistoryDocument, UpdateEntry } from '@/types/api/audit'
import type { AuditEvent, AuditRecord, FieldChange, UserInfo, ChangeGroup, SectionGroup } from '@/types/ui/audit'
import type { AuditFieldConfig } from '@/lib/audit/audit-config'
import { mapPathToSection } from '@/lib/audit/audit-config'

export type WorkerCollection = 'Workers' | 'RemovedWorkers'

/**
 * Resolves the display name of a worker so history headers read "Worker #3: Robert Pattinson"
 * instead of a bare "Worker #3". The adapter tries these in order and takes the first name that
 * resolves; an unresolved lookup leaves a bare "Worker" (never a guessed name — a wrong name in a
 * payroll audit trail is worse than the bare fallback):
 *
 *  1. `byProfileId` — used when the audit document itself carries the worker's `WorkerUserProfileId`
 *     (every worker add and every removal does). This is position-INDEPENDENT and therefore always
 *     correct, even if the Workers/RemovedWorkers array was reordered across versions.
 *  2. `byAuditPosition` — for pure field edits (e.g. a rate change) whose diff doesn't carry the id.
 *     Resolves the array index against the server-reconstructed identity map for THAT audit document
 *     (references.auditWorkers), i.e. the worker order as it stood at that exact save — not a
 *     later/final snapshot. This is why a "crowded" edit (remove one worker, edit another in the same
 *     save) still names the edited worker correctly. Absent from the map ⇒ undefined ⇒ bare label.
 */
export interface WorkerNameResolvers {
  byProfileId: (profileId: number) => string | undefined
  byAuditPosition: (auditDocId: string, collection: WorkerCollection, index: number) => string | undefined
}

/** What a version change did to a worker, used to word the history header. */
type WorkerChangeKind = 'added' | 'removed' | 'edited'

interface WorkerLabelInfo {
  /** Resolved display name, or undefined when it can't be resolved (label stays bare). */
  name?: string
  kind: WorkerChangeKind
}

/**
 * Scan a single audit document for the WorkerUserProfileId of the worker at `collection`/`index`.
 * Present whenever that worker was added or removed in this document. The id lives on the flat
 * "<collection>/<index>" entry (fieldName "WorkerUserProfileId") and, for inverted RemovedWorker
 * adds, may sit on oldValue rather than newValue — so read whichever side is populated. `isAdd` is
 * true when the id went from empty → set (a brand-new worker row), distinguishing an add from an
 * edit of an existing worker (whose id doesn't change, so it isn't in the diff at all).
 */
function findWorkerProfileIdEntryInDoc(
  doc: AuditHistoryDocument,
  collection: WorkerCollection,
  index: number
): { id: number; isAdd: boolean; isRemoval: boolean } | undefined {
  const target = `${collection}/${index}`
  for (const d of doc.differences) {
    if (d.propertyPath === target && d.fieldName === 'WorkerUserProfileId') {
      const id = d.newValue ?? d.oldValue
      if (typeof id === 'number') {
        const oldEmpty = d.oldValue === null || d.oldValue === undefined
        const newEmpty = d.newValue === null || d.newValue === undefined
        return { id, isAdd: oldEmpty && !newEmpty, isRemoval: !oldEmpty && newEmpty }
      }
    }
  }
  return undefined
}

/**
 * Transform raw audit history documents into version-grouped events
 * Each version becomes one AuditEvent with multiple AuditRecords
 */
export function transformAuditHistory(
  documents: AuditHistoryDocument[],
  config: AuditFieldConfig,
  validEntityIds?: string[],
  getProfileName: (id: number) => string = (id) => `Profile ID ${id}`,
  resolveWorkerName?: WorkerNameResolvers
): AuditEvent[] {
  if (!documents || documents.length === 0) {
    return []
  }

  // Bind worker-name resolution to a single document so field-change mapping only needs the
  // propertyPath. Prefer the worker's own id when this document records it (position-independent);
  // otherwise resolve by array position against this document's own reconstructed identity map.
  // Also classify the change (added/removed/edited) so the header can be worded accordingly. See
  // WorkerNameResolvers.
  const parentWorkerLabelResolver = (doc: AuditHistoryDocument) => {
    if (!resolveWorkerName) return undefined
    return (propertyPath: string): WorkerLabelInfo | undefined => {
      const match = propertyPath.match(/^(Workers|RemovedWorkers)\/(\d+)/)
      if (!match) return undefined
      const collection = match[1] as WorkerCollection
      const index = parseInt(match[2], 10)

      const idEntry = findWorkerProfileIdEntryInDoc(doc, collection, index)
      const name = idEntry !== undefined
        // The worker's own id is recorded in this document (add/removal) — authoritative and
        // position-independent.
        ? resolveWorkerName.byProfileId(idEntry.id)
        // Pure edit (no id in the diff): resolve by position against this document's own at-save
        // worker order (server-reconstructed), so a crowded edit still names the right worker.
        : resolveWorkerName.byAuditPosition(doc.id, collection, index)

      // A worker leaving the active Workers array (id -> null) is a removal too, not an edit —
      // even though it's on the Workers collection rather than RemovedWorkers.
      const kind: WorkerChangeKind =
        collection === 'RemovedWorkers' || idEntry?.isRemoval ? 'removed'
          : idEntry?.isAdd ? 'added'
          : 'edited'
      return { name, kind }
    }
  }

  const validIdSet = validEntityIds ? new Set(validEntityIds) : null

  // Group documents by both version and entityGuid to separate deleted versions
  const byVersionAndGuid = new Map<string, AuditHistoryDocument[]>()
  documents.forEach(doc => {
    const key = `${doc.entityVersion}-${doc.entityGuid || 'unknown'}`
    if (!byVersionAndGuid.has(key)) {
      byVersionAndGuid.set(key, [])
    }
    byVersionAndGuid.get(key)!.push(doc)
  })

  const events: AuditEvent[] = []

  // Process each version+guid group - create ONE event per unique version instance
  byVersionAndGuid.forEach((versionDocs, key) => {
    // Sort by timestamp within version (oldest first)
    const sorted = [...versionDocs].sort(
      (a, b) => new Date(a.createdDateTime).getTime() - new Date(b.createdDateTime).getTime()
    )

    // Use the latest audit for top-level info
    const latestAudit = sorted[sorted.length - 1]
    const firstAudit = sorted[0]

    // Determine if this version was deleted (no longer exists in current versions)
    const isDeleted = validIdSet 
      ? (latestAudit.entityGuid ? !validIdSet.has(latestAudit.entityGuid) : true)
      : false

    // Convert each audit document to an AuditRecord
    const auditRecords: AuditRecord[] = sorted.map(doc => {
      const resolveWorkerLabel = parentWorkerLabelResolver(doc)
      const preprocessed = reorderRemovedWorkerFields(
        suppressMirroredWorkerRemovals(
          collapseSimpleArrayDiffs(dropDiffSlotIndices(doc.differences))
        )
      )
      const changes = preprocessed
        .filter(diff => !isHiddenField(diff, config))
        .map(diff => mapUpdateEntryToFieldChange(diff, config, resolveWorkerLabel))
      const sectionGroups = groupChangesBySectionAndParent(changes, config)

      return {
        id: doc.id,
        status: config.formatStatus(doc.entityStatusCode),
        action: config.formatAction(doc.entityAction),
        comment: doc.comment,
        modifiedBy: createUserInfo(doc.createdByProfileId, doc.createdByUserName, getProfileName),
        modifiedDateTime: doc.lastModifiedDateTime,
        changes,
        sectionGroups,
        workflowInstanceId: doc.workflowInstanceId,
        isLatest: doc.id === latestAudit.id,
      }
    })

    // Determine if this version had any status changes
    const statuses = new Set(sorted.map(d => d.entityStatusCode))
    const isStatusChange = statuses.size > 1

    // Combine all changes from all audits in this version (used for filtering/search, not the
    // rendered diff).
    const allChanges = sorted.flatMap(doc => {
      const resolveWorkerLabel = parentWorkerLabelResolver(doc)
      return reorderRemovedWorkerFields(
        suppressMirroredWorkerRemovals(
          collapseSimpleArrayDiffs(dropDiffSlotIndices(doc.differences))
        )
      )
        .filter(diff => !isHiddenField(diff, config))
        .map(diff => mapUpdateEntryToFieldChange(diff, config, resolveWorkerLabel))
    })

    const event: AuditEvent = {
      id: latestAudit.id,
      entityType: latestAudit.entityType,
      entityId: latestAudit.entityId.toString(),
      version: latestAudit.entityVersion,
      status: config.formatStatus(latestAudit.entityStatusCode),
      statusBefore: config.formatStatus(firstAudit.entityStatusCode),
      statusAfter: config.formatStatus(latestAudit.entityStatusCode),
      comment: firstAudit.comment,
      modifiedBy: createUserInfo(latestAudit.createdByProfileId, latestAudit.createdByUserName, getProfileName),
      modifiedDateTime: latestAudit.lastModifiedDateTime,
      changes: allChanges,
      isStatusChange,
      workflowInstanceId: latestAudit.workflowInstanceId,
      audits: auditRecords,
      isDeleted,
    }

    events.push(event)
  })

  return events
}

/**
 * Pre-processor that drops the slot index the audit diff adds when a nested array replaces a null: it
 * records the change as an [old, new] pair and paths the new elements from inside it, so the first
 * entry added to a worker's empty list arrives as "Workers/0/ScheduledBudgetedHours/1/0" instead of
 * "Workers/0/ScheduledBudgetedHours/0". No engagement field is an array of arrays, so two indices in a
 * row only ever mean this, and the first of them is the slot.
 */
function dropDiffSlotIndices(differences: UpdateEntry[]): UpdateEntry[] {
  return differences.map(diff => {
    const propertyPath = diff.propertyPath?.replace(/\/\d+(?=\/\d+(?:\/|$))/g, '')
    return propertyPath === diff.propertyPath ? diff : { ...diff, propertyPath }
  })
}

/**
 * Pre-processor that collapses per-index diffs of simple-value arrays (e.g. int[] / string[]) into
 * a single diff per array. The Phoenix audit framework emits each element as its own entry with
 * propertyPath ending in "/<index>" and fieldName "" — those are useless one-row groups in the
 * UI. We detect the pattern and merge them so the array shows up as a single field change with
 * the values collected into an array (formatValue then joins them for display and resolves
 * lookups per element).
 */
function collapseSimpleArrayDiffs(differences: UpdateEntry[]): UpdateEntry[] {
  // Walk the input once and emit entries in original order. For bucketed entries, we reserve the
  // slot at the FIRST occurrence so the collapsed entry lands next to its siblings on the same
  // parent object (e.g. TerminationReasonIds sits between TerminationTypeId and Comments instead
  // of getting tacked on at the end of the parent's section).
  const arrayBuckets = new Map<string, UpdateEntry[]>()
  const ordered: Array<UpdateEntry | { bucketKey: string }> = []

  for (const diff of differences) {
    // Simple-value array element pattern: propertyPath ends with "/<digits>" AND fieldName is empty.
    // Complex-array element diffs (e.g. Workers/0/JobTitle) have fieldName populated and fall through.
    const indexMatch = diff.fieldName === '' && diff.propertyPath.match(/^(.+)\/(\d+)$/)
    if (indexMatch) {
      const basePath = indexMatch[1]
      if (!arrayBuckets.has(basePath)) {
        arrayBuckets.set(basePath, [])
        ordered.push({ bucketKey: basePath })
      }
      arrayBuckets.get(basePath)!.push(diff)
      continue
    }
    ordered.push(diff)
  }

  return ordered.map(item => {
    if ('bucketKey' in item) {
      const basePath = item.bucketKey
      const entries = arrayBuckets.get(basePath)!
      // basePath is "RemovedWorkers/0/TerminationReasonIds"; split into propertyPath + fieldName so
      // the parent grouping treats it like any other field on RemovedWorkers/0.
      const lastSlash = basePath.lastIndexOf('/')
      const newPropertyPath = lastSlash >= 0 ? basePath.substring(0, lastSlash) : ''
      const newFieldName = lastSlash >= 0 ? basePath.substring(lastSlash + 1) : basePath

      const oldValues = entries.map(e => e.oldValue).filter(v => v !== null && v !== undefined)
      const newValues = entries.map(e => e.newValue).filter(v => v !== null && v !== undefined)

      return {
        propertyPath: newPropertyPath,
        fieldName: newFieldName,
        displayName: null,
        oldValue: oldValues.length > 0 ? oldValues : null,
        newValue: newValues.length > 0 ? newValues : null,
        isSecretField: entries.some(e => e.isSecretField),
      }
    }
    return item
  })
}

/**
 * Phoenix's audit framework computes oldValue via `newValue.Previous` (JToken sibling order in the
 * parsed JSON, not semantic prior state). For RemovedWorkers/* additions the quirk sometimes
 * inverts the columns — data lands in oldValue with newValue null instead of the natural ADD
 * shape (null → data). Other terminate scenarios serialize correctly. Both adds are semantically
 * "this worker was just terminated," so detect the inverted shape specifically and flip back.
 * Do not touch entries where newValue holds the data (already correct) or where both sides hold
 * data (genuine update of the RemovedWorker record).
 */
function isRemovedWorkerPath(propertyPath: string): boolean {
  return propertyPath.startsWith('RemovedWorkers/') || propertyPath === 'RemovedWorkers'
}

function isInvertedRemovedWorkerAdd(entry: UpdateEntry): boolean {
  return isRemovedWorkerPath(entry.propertyPath)
    && entry.oldValue !== null && entry.oldValue !== undefined
    && (entry.newValue === null || entry.newValue === undefined)
}

/**
 * When a worker is terminated, the diff stream contains BOTH a Workers/<n> removal AND a
 * RemovedWorkers/<m> addition for the same WorkerUserProfileId. They describe the same logical
 * event (worker moved from active → removed) but render as two redundant sections — the
 * RemovedWorker entry already carries every worker field plus the termination-specific ones.
 * Suppress the Workers/<n> entries when a matching RemovedWorkers entry exists, so the user
 * sees one consolidated "this worker was terminated" record.
 */
function getWorkersSetKey(propertyPath: string): string | null {
  // Match both the flat "Workers/<n>" entries (where WorkerUserProfileId etc. live) and any
  // nested "Workers/<n>/..." entries (e.g. Workers/0/TimeBasedPay). Both belong to the same
  // logical worker record and must be considered together for suppression.
  const match = propertyPath.match(/^Workers\/(\d+)(?:\/.*)?$/)
  return match ? `Workers/${match[1]}` : null
}

function suppressMirroredWorkerRemovals(differences: UpdateEntry[]): UpdateEntry[] {
  // Pass 1: collect Workers/<n> sets (including nested fields) and their WorkerUserProfileId;
  // collect RemovedWorker IDs.
  const workersByIndex = new Map<string, { entries: UpdateEntry[]; userProfileId: number | null }>()
  const removedWorkerIds = new Set<number>()

  for (const diff of differences) {
    const workersKey = getWorkersSetKey(diff.propertyPath)
    if (workersKey !== null) {
      let bucket = workersByIndex.get(workersKey)
      if (!bucket) {
        bucket = { entries: [], userProfileId: null }
        workersByIndex.set(workersKey, bucket)
      }
      bucket.entries.push(diff)
      // WorkerUserProfileId lives on the flat Workers/<n> entry only — nested entries won't
      // populate this.
      if (diff.propertyPath === workersKey && diff.fieldName === 'WorkerUserProfileId' && typeof diff.oldValue === 'number') {
        bucket.userProfileId = diff.oldValue
      }
      continue
    }

    const removedMatch = diff.propertyPath.match(/^RemovedWorkers\/(\d+)$/)
    if (removedMatch && diff.fieldName === 'WorkerUserProfileId') {
      // The ID lives on whichever side the framework populated for this add.
      const id = (diff.newValue ?? diff.oldValue)
      if (typeof id === 'number') {
        removedWorkerIds.add(id)
      }
    }
  }

  // Pass 2: a Workers/<n> set is suppressible only if (a) every field is a pure removal
  // (oldValue populated, newValue empty) — meaning the worker was wholly removed from the array,
  // not just edited — AND (b) the same WorkerUserProfileId shows up in the RemovedWorkers add.
  const suppressedKeys = new Set<string>()
  for (const [key, bucket] of workersByIndex) {
    if (bucket.userProfileId === null) continue
    if (!removedWorkerIds.has(bucket.userProfileId)) continue
    const allPureRemovals = bucket.entries.every(e =>
      e.oldValue !== null && e.oldValue !== undefined &&
      (e.newValue === null || e.newValue === undefined)
    )
    if (allPureRemovals) {
      suppressedKeys.add(key)
    }
  }

  if (suppressedKeys.size === 0) return differences
  return differences.filter(diff => {
    const key = getWorkersSetKey(diff.propertyPath)
    return key === null || !suppressedKeys.has(key)
  })
}

/**
 * Stable-sort fields within each RemovedWorkers/<n> parent so all termination-related fields
 * cluster together near the top of the section, regardless of the order the audit framework
 * happened to emit them. Other parents (Workers, Milestones, etc.) keep their natural API order.
 */
const REMOVED_WORKER_FIELD_PRIORITY: Record<string, number> = {
  LastDayOfWork: 0,
  TerminationTypeId: 1,
  TerminationReasonIds: 2,
  Comments: 3,
}

function reorderRemovedWorkerFields(differences: UpdateEntry[]): UpdateEntry[] {
  // Stable group by propertyPath, sort within RemovedWorkers/<n> groups, flatten preserving
  // original group encounter order.
  const groups: { propertyPath: string; entries: UpdateEntry[] }[] = []
  const groupIndex = new Map<string, number>()

  for (const diff of differences) {
    let idx = groupIndex.get(diff.propertyPath)
    if (idx === undefined) {
      idx = groups.length
      groups.push({ propertyPath: diff.propertyPath, entries: [] })
      groupIndex.set(diff.propertyPath, idx)
    }
    groups[idx].entries.push(diff)
  }

  for (const group of groups) {
    if (!/^RemovedWorkers\/\d+$/.test(group.propertyPath)) continue
    const decorated = group.entries.map((e, i) => ({ e, i }))
    decorated.sort((a, b) => {
      const pa = REMOVED_WORKER_FIELD_PRIORITY[a.e.fieldName] ?? 100
      const pb = REMOVED_WORKER_FIELD_PRIORITY[b.e.fieldName] ?? 100
      if (pa !== pb) return pa - pb
      return a.i - b.i
    })
    group.entries = decorated.map(d => d.e)
  }

  return groups.flatMap(g => g.entries)
}

/** Whether the config leaves this field out of the history. Paths compare with array indices removed. */
function isHiddenField(entry: UpdateEntry, config: AuditFieldConfig): boolean {
  if (!config.hiddenFields?.length) return false
  const fullPath = entry.propertyPath ? `${entry.propertyPath}/${entry.fieldName}` : entry.fieldName
  return config.hiddenFields.includes(fullPath.replace(/\/\d+(?=\/|$)/g, ''))
}

/**
 * Convert UpdateEntry to FieldChange with formatted values
 */
function mapUpdateEntryToFieldChange(
  entry: UpdateEntry,
  config: AuditFieldConfig,
  resolveWorkerLabel?: (propertyPath: string) => WorkerLabelInfo | undefined
): FieldChange {
  const swapColumns = isInvertedRemovedWorkerAdd(entry)
  const propertyPath = entry.propertyPath
  const fieldName = entry.fieldName
  const oldValue = swapColumns ? entry.newValue : entry.oldValue
  const newValue = swapColumns ? entry.oldValue : entry.newValue

  // Transform fieldName to display-friendly version using config
  // Try full path first (e.g., "Workers/WorkerUserProfileId"), then bare fieldName
  const fieldFullPath = propertyPath ? `${propertyPath}/${fieldName}` : fieldName
  const normalizedFieldPath = fieldFullPath.replace(/\/\d+\//g, '/') // Remove array indices
  const displayFieldName = config.fieldLabels[normalizedFieldPath] || config.fieldLabels[fieldName] || fieldName

  // Build parent label from propertyPath if it exists
  let parentLabel: string | undefined = undefined
  let parentParts: string[] = []
  if (config.formatPathSegment && propertyPath) {
    // Split path and format each segment (numbers are indices, others are property names)
    const segments = propertyPath.split('/')
    const formatted: Array<{ value: string; isIndex: boolean }> = []
    
    for (const seg of segments) {
      const isIndex = /^\d+$/.test(seg)
      const formattedSeg = config.formatPathSegment!(seg, isIndex)
      if (formattedSeg !== undefined) {
        formatted.push({ value: formattedSeg, isIndex })
      }
    }
    
    // Join segments: use space between array name and index, slash otherwise
    if (formatted.length > 0) {
      const parts: string[] = []
      for (let i = 0; i < formatted.length; i++) {
        if (i === 0) {
          parts.push(formatted[i].value)
        } else {
          // If current segment is an index, join with space to previous part
          // Otherwise, join with slash
          if (formatted[i].isIndex) {
            parts[parts.length - 1] += ` ${formatted[i].value}`
          } else {
            parts.push(formatted[i].value)
          }
        }
      }
      parentLabel = parts.join(' / ')
      parentParts = parts
    }
  }

  // Tag worker parent labels with the worker's name and reword by change kind (GW-310). When the
  // name resolves the array index is dropped — the name identifies the worker and the position isn't
  // meaningful (unlike approver sequence, which keeps its "#N" via the generic path below):
  //   • added   -> "Added Worker: Robert Pattinson"
  //   • removed -> "Removed Worker: Robert Pattinson"
  //   • edited  -> "Updated Worker: Robert Pattinson"
  // When the name CANNOT be resolved (deleted version, reconstruction bailed, profile missing) the
  // indexed label is KEPT ("Worker #3") — never a guessed name, and crucially the index keeps two
  // distinct unresolved workers in the same document from collapsing into one merged group.
  // Only applies to Workers/RemovedWorkers paths (resolveWorkerLabel returns undefined otherwise).
  // The worker is always the first part, and anything nested under them (a pay rate, a scheduled hours
  // change) follows with its own index, which is kept: "Worker #3 / Pay Rate #2" becomes
  // "Updated Worker: Robert Pattinson / Pay Rate #2".
  if (parentLabel && resolveWorkerLabel) {
    const info = resolveWorkerLabel(propertyPath)
    if (info?.name) {
      const baseWithoutIndex = parentParts[0].replace(/\s*#\d+$/, '') // "Worker #3" -> "Worker"
      const nested = parentParts.slice(1).map(part => ` / ${part}`).join('')
      if (info.kind === 'added') {
        parentLabel = `Added ${baseWithoutIndex}: ${info.name}${nested}`
      } else if (info.kind === 'removed') {
        // A removal off the active Workers array has base "Worker"; the RemovedWorkers collection
        // already reads "Removed Worker". Normalise both to "Removed Worker: <name>".
        parentLabel = baseWithoutIndex.startsWith('Removed')
          ? `${baseWithoutIndex}: ${info.name}${nested}`
          : `Removed ${baseWithoutIndex}: ${info.name}${nested}`
      } else {
        parentLabel = `Updated ${baseWithoutIndex}: ${info.name}${nested}`
      }
    }
    // info without a name → leave the generic indexed label untouched (see above).
  }

  // Get section from config
  // Construct full path: "CoreInfo" + "BillingTypeCode" -> "CoreInfo/BillingTypeCode"
  const fullPath = propertyPath ? `${propertyPath}/${fieldName}` : fieldName
  const sectionPath = mapPathToSection(fullPath, config.availableSections)

  // Format values using config
  const formattedOldValue = oldValue !== null && oldValue !== undefined
    ? config.formatValue(oldValue, propertyPath, fieldName)
    : ''
  
  const formattedNewValue = newValue !== null && newValue !== undefined
    ? config.formatValue(newValue, propertyPath, fieldName)
    : ''

  return {
    fieldPath: propertyPath,
    fieldName: displayFieldName, // Just the field name (e.g., "Start Date")
    displayName: displayFieldName, // Keep for compatibility but not used
    sectionPath,
    parentLabel, // Parent context (e.g., "Milestone #1", "Revenue Recognition / Period #1")
    oldValue,
    newValue,
    formattedOldValue,
    formattedNewValue,
  }
}

/**
 * Create UserInfo from profile ID
 */
function createUserInfo(
  profileId: number, 
  email: string,
  getProfileName?: (id: number) => string
): UserInfo {
  return {
    profileId,
    displayName: getProfileName ? getProfileName(profileId) : email, // Use profile name if available, fallback to email
  }
}

/** A change side that carries no information (used to drop null↔null rows). */
function isEmptyValue(value: unknown): boolean {
  return value === null
    || value === undefined
    || value === ''
    || (Array.isArray(value) && value.length === 0)
}

/**
 * Group changes by section first, then by parentLabel within each section
 * Returns sections ordered by config.availableSections, with change groups ordered (no-parent first)
 */
function groupChangesBySectionAndParent(changes: FieldChange[], config: AuditFieldConfig): SectionGroup[] {
  const filteredChanges = changes
    // Filter out status changes.
    .filter((c) => c.fieldPath !== "status")
    // Drop rows with nothing on either side — a real change always has one populated side. These
    // arise from historical records where an empty nested object (e.g. T4SalariedPay on an hourly
    // worker, or TimeBasedPay on a salaried one) leaked through as a null↔null row.
    .filter((c) => !isEmptyValue(c.oldValue) || !isEmptyValue(c.newValue))
  
  // Group by section
  const bySection = filteredChanges.reduce((acc, change) => {
    const section = change.sectionPath
    if (!acc[section]) acc[section] = []
    acc[section].push(change)
    return acc
  }, {} as Record<string, FieldChange[]>)

  // Create ordered sections based on config.availableSections
  const sectionGroups: SectionGroup[] = []
  
  const sortedSections = Object.keys(bySection).sort((a, b) => {
    const indexA = config.availableSections.findIndex(s => s.value === a)
    const indexB = config.availableSections.findIndex(s => s.value === b)
    
    if (indexA === -1 && indexB === -1) return 0
    if (indexA === -1) return 1
    if (indexB === -1) return -1
    
    return indexA - indexB
  })

  sortedSections.forEach(section => {
    const sectionChanges = bySection[section]
    
    // Group by parentLabel within this section
    const noParentChanges: FieldChange[] = []
    const byParent = new Map<string, FieldChange[]>()
    
    sectionChanges.forEach(change => {
      if (!change.parentLabel) {
        noParentChanges.push(change)
      } else {
        if (!byParent.has(change.parentLabel)) {
          byParent.set(change.parentLabel, [])
        }
        byParent.get(change.parentLabel)!.push(change)
      }
    })

    // Create ordered change groups: no-parent first, then parent groups
    const changeGroups: ChangeGroup[] = []
    
    if (noParentChanges.length > 0) {
      changeGroups.push({
        parentLabel: undefined,
        changes: noParentChanges
      })
    }
    
    byParent.forEach((parentChanges, parentLabel) => {
      changeGroups.push({
        parentLabel,
        changes: parentChanges
      })
    })

    sectionGroups.push({
      section,
      changeGroups
    })
  })

  return sectionGroups
}

